import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, BookPlus, Send } from "lucide-react";
import { fmtDate } from "@/lib/dates";

export default function Team() {
  const [staff, setStaff] = useState([]);
  const [courses, setCourses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", job_role: "Care worker", course_ids: [] });
  const defaultCourseIds = (list) => list.filter((c) => /essential oral care/i.test(c.title)).map((c) => c.id);
  const toggleFormCourse = (id) => setForm((f) => ({ ...f, course_ids: f.course_ids.includes(id) ? f.course_ids.filter((x) => x !== id) : [...f.course_ids, id] }));
  const [assign, setAssign] = useState({ course_id: "", user_ids: [] });
  const [busy, setBusy] = useState(false);
  const [home, setHome] = useState(null);
  const [filter, setFilter] = useState("active");
  const [confirmStaff, setConfirmStaff] = useState(null);
  const isActive = (s) => s.active !== false;
  const activeStaff = staff.filter(isActive);
  const inactiveCount = staff.length - activeStaff.length;
  const visibleStaff = filter === "all" ? staff : filter === "inactive" ? staff.filter((s) => !isActive(s)) : activeStaff;
  const atLimit = !!(home?.trial && activeStaff.length >= home.staff_limit);

  const loadStaff = useCallback(() => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}), []);
  useEffect(() => { loadStaff(); api.get("/courses").then((r) => { setCourses(r.data); setForm((f) => ({ ...f, course_ids: defaultCourseIds(r.data) })); }).catch(() => {}); api.get("/care-home/me").then((r) => setHome(r.data)).catch(() => {}); }, [loadStaff]);

  const addStaff = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/staff", form);
      const n = data.assigned || 0;
      toast.success(`Invite sent to ${data.email}`, { description: n ? `${n} course${n > 1 ? "s" : ""} assigned — one email with their password link and training.` : "They'll receive a link to set their own password." });
      setForm({ name: "", email: "", job_role: "Care worker", course_ids: defaultCourseIds(courses) });
      setAddOpen(false); loadStaff();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resendInvite = async (s) => {
    try { const { data } = await api.post(`/staff/${s.id}/resend-invite`); toast.success(`Invite re-sent to ${data.email}`); loadStaff(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const doAssign = async () => {
    if (!assign.course_id || assign.user_ids.length === 0) { toast.info("Pick a course and at least one staff member."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/enrollments/assign", assign);
      toast.success(`Assigned to ${data.assigned} staff`, { description: data.assigned ? "Each person has been emailed a link to start the course." : "Everyone selected already had this course." });
      setAssignOpen(false); setAssign({ course_id: "", user_ids: [] });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (s, active) => {
    try {
      await api.patch(`/staff/${s.id}/status`, { active });
      toast.success(active ? `${s.name} has been reactivated` : `${s.name} has been deactivated`, { description: active ? "They can log in again." : "They can no longer log in. Their training records are kept." });
      loadStaff(); api.get("/care-home/me").then((r) => setHome(r.data)).catch(() => {});
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const onToggle = (s, checked) => (checked ? setStatus(s, true) : setConfirmStaff(s));

  const toggleUser = (id) => setAssign((a) => ({ ...a, user_ids: a.user_ids.includes(id) ? a.user_ids.filter((x) => x !== id) : [...a.user_ids, id] }));

  return (
    <div>
      <PageHeader title="My team" subtitle="Add staff logins and assign training courses.">
        <div className="flex gap-2">
          <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
            <DialogTrigger asChild><Button data-testid="assign-open-btn" variant="outline" className="rounded-full"><BookPlus className="h-4 w-4 mr-2" />Assign course</Button></DialogTrigger>
            <DialogContent data-testid="assign-dialog">
              <DialogHeader><DialogTitle>Assign a course</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Course</Label>
                  <Select value={assign.course_id} onValueChange={(v) => setAssign({ ...assign, course_id: v })}>
                    <SelectTrigger data-testid="assign-course-select"><SelectValue placeholder="Select a course" /></SelectTrigger>
                    <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Staff</Label>
                  <div className="mt-2 space-y-2 max-h-52 overflow-auto">
                    {activeStaff.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox data-testid={`assign-user-${s.id}`} checked={assign.user_ids.includes(s.id)} onCheckedChange={() => toggleUser(s.id)} />{s.name} <span className="text-muted-foreground">· {s.job_role}</span>
                      </label>
                    ))}
                    {activeStaff.length === 0 && <p className="text-sm text-muted-foreground">Add staff first.</p>}
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="assign-submit-btn" onClick={doAssign} disabled={busy} className="rounded-full bg-brand text-white">Assign</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button data-testid="add-staff-open-btn" disabled={atLimit} className="rounded-full bg-brand hover:bg-brand-hover text-white"><UserPlus className="h-4 w-4 mr-2" />Add staff</Button></DialogTrigger>
            <DialogContent data-testid="add-staff-dialog">
              <DialogHeader><DialogTitle>Add a staff member</DialogTitle><DialogDescription>They'll get one email with a secure link to choose their password and their assigned training (link valid for 7 days).</DialogDescription></DialogHeader>
              <form onSubmit={addStaff} className="space-y-4">
                <div><Label>Name</Label><Input data-testid="staff-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input data-testid="staff-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Role</Label><Input data-testid="staff-role" value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })} /></div>
                <div>
                  <Label>Assign training</Label>
                  <div className="mt-1.5 space-y-2 rounded-xl border border-cream-deep bg-cream p-3 max-h-48 overflow-y-auto" data-testid="staff-courses">
                    {courses.map((c) => (
                      <label key={c.id} className="flex items-start gap-2 text-sm cursor-pointer">
                        <Checkbox data-testid={`staff-course-${c.id}`} checked={form.course_ids.includes(c.id)} onCheckedChange={() => toggleFormCourse(c.id)} className="mt-0.5" />
                        <span>{c.title}{c.cpd_hours ? <span className="text-muted-foreground"> · {c.cpd_hours} CPD hrs</span> : null}</span>
                      </label>
                    ))}
                    {courses.length === 0 && <span className="text-xs text-muted-foreground">No courses available yet.</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Due 30 days from today. You can assign more later with "Assign course".</p>
                </div>
                <DialogFooter><Button data-testid="staff-submit" type="submit" disabled={busy} className="rounded-full bg-coral text-white">{form.course_ids.length ? "Add & send invite with training" : "Add & send invite"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </PageHeader>

      {home?.trial && (
        <div data-testid="trial-staff-limit" className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${atLimit ? "border-coral/40 bg-coral/5 text-coral" : "border-cream-deep bg-cream-card text-muted-foreground"}`}>
          <span>Free trial: <b>{activeStaff.length} of {home.staff_limit}</b> staff logins used.{atLimit ? " Subscribe to add your whole team." : ""}</span>
          <Link to="/app/billing" data-testid="trial-upgrade-link" className="font-semibold text-brand underline">See plans</Link>
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2" data-testid="staff-filter">
        {[["active", "Active", activeStaff.length], ["inactive", "Inactive", inactiveCount], ["all", "All", staff.length]].map(([key, label, n]) => (
          <button key={key} type="button" data-testid={`staff-filter-${key}`} aria-pressed={filter === key} onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${filter === key ? "border-brand bg-brand text-white" : "border-cream-deep bg-cream-card text-muted-foreground hover:text-brand"}`}>
            {label} ({n})
          </button>
        ))}
      </div>

      <div className="bg-cream-card border border-cream-deep rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Login</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {visibleStaff.map((s, i) => (
              <TableRow key={s.id} data-testid={`staff-row-${i}`} className={`${i % 2 ? "bg-cream/50" : ""} ${isActive(s) ? "" : "opacity-60"}`}>
                <TableCell className="font-medium">{s.name}</TableCell><TableCell className="text-muted-foreground">{s.email}</TableCell><TableCell>{s.job_role}</TableCell>
                <TableCell>
                  {!isActive(s) ? <span className="text-xs text-muted-foreground" data-testid={`staff-inactive-${i}`}>Deactivated{s.deactivated_at ? ` ${fmtDate(s.deactivated_at)}` : ""}</span>
                    : s.last_login_at ? <span className="text-xs text-rag-green font-medium" data-testid={`staff-active-${i}`}>Active · last login {fmtDate(s.last_login_at)}</span>
                    : <div className="flex items-center gap-2 text-xs text-muted-foreground"><span data-testid={`staff-pending-${i}`}>Not logged in yet{s.invite_sent_at ? ` · invited ${fmtDate(s.invite_sent_at)}` : ""}</span>
                        <button type="button" data-testid={`staff-resend-${i}`} onClick={() => resendInvite(s)} className="inline-flex items-center gap-1 text-brand font-semibold hover:text-coral"><Send className="h-3 w-3" />Resend invite</button></div>}
                </TableCell>
                <TableCell>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch data-testid={`staff-status-toggle-${i}`} checked={isActive(s)} onCheckedChange={(c) => onToggle(s, c)}
                      aria-label={isActive(s) ? `Deactivate ${s.name}` : `Reactivate ${s.name}`} />
                    <span className={`text-xs font-medium ${isActive(s) ? "text-rag-green" : "text-muted-foreground"}`}>{isActive(s) ? "Active" : "Inactive"}</span>
                  </label>
                </TableCell>
              </TableRow>
            ))}
            {visibleStaff.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">{staff.length === 0 ? "No staff yet — add your first team member." : `No ${filter} staff.`}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmStaff} onOpenChange={(o) => !o && setConfirmStaff(null)}>
        <AlertDialogContent data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {confirmStaff?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They won't be able to log in. Their training records and certificates will be kept, and you can reactivate them at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="deactivate-confirm" onClick={() => { const s = confirmStaff; setConfirmStaff(null); setStatus(s, false); }} className="bg-coral hover:bg-coral-hover text-white">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
